import React, { useEffect, useState } from "react";
import TwoColumnLayout from "../layouts/TwoColumnLayout";
import { useHistory, useParams } from "react-router-dom";
import { getUser } from "../utils/auth";
import { isZh } from "../utils/language";
import PERMISSIONS, { apiFetch, fetchLunarForIntDate } from "../utils/wkCommon";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import AgendaBookMainPanel from "../components/AgendaBookMainPanel";
import AgendaBookHeaderActions from "../components/AgendaBookHeaderActions";
import AgendaBookModals from "../components/AgendaBookModals";
import { handleLoadTemplateUtil, handleSaveTemplateUtil, buildInitialAgendaAttributes } from "../utils/agendaBookUtils";
import "../styles/agendaBook.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgendaBook() {
  const user = getUser();
  const history = useHistory();
  const { id } = useParams(); // optional: /agendabook/:id

  const [selectedAgenda, setSelectedAgenda] = useState(null);

  const [agendaBook, setAgendaBook] = useState(null);
  const [agendaBookReady, setAgendaBookReady] = useState(false);

  const [lunarInfo, setLunarInfo] = useState(null);

  const [activeTab, setActiveTab] = useState(getInitialTab());

  const [canSaveTemplate, setCanSaveTemplate] = useState(false);
  const [canReadAgendaRundown, setCanReadAgendaRundown] = useState(false);
  const [canReadAgendaPersonnel, setCanReadAgendaPersonnel] = useState(false);
  const [canReadAgendaExpense, setCanReadAgendaExpense] = useState(false);
  const [permsReady, setPermsReady] = useState(false);

  const [showMenu, setShowMenu] = useState(false);
  const [showAttrModal, setShowAttrModal] = useState(false);
  
  function handleSaveTemplate() {
    return handleSaveTemplateUtil({
      saveMode,
      selectedTemplateId,
      templateZh,
      templateId,
      agendaBook,
      setShowSaveModal,
      isZh
    });
  }

  function handleLoadTemplate() {
    return handleLoadTemplateUtil({
      selectedTemplateId,
      templates,
      agendaBook,
      setAgendaBook,
      setShowLoadModal,
      isZh
    });
  }

  /* ================= LOAD ACTIVE TAB ================= */

  function getInitialTab() {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab");
  }

  function handleRundownUpdated(newRundowns) {
    setAgendaBook(prev => ({
      ...prev,
      agendaRundowns: newRundowns
    }));
  }

  function handlePersonnelUpdated(newData) {
    setAgendaBook(prev => ({
      ...prev,
      agendaPersonnels: newData
    }));
  }

  function handleFinanceUpdated(newData) {
    setAgendaBook(prev => ({
      ...prev,
      agendaFinances: newData
    }));
  }

  // Listener for AttributesConfigModal
  useEffect(() => {
    function handleReload() {
      if (!id || !agendaBook?.documentId) return;

      apiFetch(`/api/agenda-books/${agendaBook.documentId}?populate=*`)
        .then(resp => {
          setAgendaBook(resp?.data || resp);
        });
    }

    window.addEventListener("agendabook-updated", handleReload);

    return () => {
      window.removeEventListener("agendabook-updated", handleReload);
    };
  }, [agendaBook?.documentId, id]);

  async function reloadAgendaBook() {
    if (!agendaBook?.documentId) return;

    const resp = await apiFetch(
      `/api/agenda-books/${agendaBook.documentId}?populate=*`
    );

    setAgendaBook(resp?.data || resp);
  }

  /* ================= LOAD PERMISSIONS ================= */
  useEffect(() => {
    let mounted = true;

    async function loadPerms() {
      try {
        const email = user?.email || user?.LoginEmail;

        if (mounted)
          setCanReadAgendaRundown(
            Boolean(await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_RUNDOWN_READ))
          );

        if (mounted)
          setCanReadAgendaPersonnel(
            Boolean(await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_PERSONNEL_READ))
          );

        if (mounted)
          setCanReadAgendaExpense(
            Boolean(await hasUmatPermissionAsync(email, PERMISSIONS.AGENDA_EXPENSE_READ))
          );

        if (mounted)
          setCanSaveTemplate(
            Boolean(await hasUmatPermissionAsync(email, PERMISSIONS.AGENDABOOK_SAVETEMPLATE))
          );
      } catch (e) {
        console.warn("loadPerms failed", e);
      } finally {
        if (mounted) setPermsReady(true);
      }
    }

    loadPerms();
    return () => { mounted = false; };
  }, [user]);

  /* ================= BUILD TABS ================= */
  const tabs = [];
  if (canReadAgendaRundown) tabs.push({ key: "rundown", label: (isZh() ? "流程" : "Susunan Acara") });
  if (canReadAgendaPersonnel) tabs.push({ key: "personnel", label: (isZh() ? "辦事" : "Susunan Petugas") });
  if (canReadAgendaExpense) tabs.push({ key: "finance", label: (isZh() ? "收支表" : "Keuangan") });

    useEffect(() => {
    let mounted = true;

    async function loadAgenda() {
        if (!id) return;

        try {
        const resp = await apiFetch(`/api/agendas/${id}?populate[0]=agenda_pre_reg_form`);
        const agendaData = resp?.data || resp;

        if (mounted) {
            setSelectedAgenda(agendaData);

            // ===== load lunar date =====
            if (agendaData?.agendaDate) {
                fetchLunarForIntDate(agendaData.agendaDate)
                .then(setLunarInfo)
                .catch(() => setLunarInfo(null));
            }

            // set tab pertama yang boleh diakses
            if (!activeTab && tabs.length > 0) {
            setActiveTab(tabs[0].key);
            }
        }
        } catch (e) {
        console.warn("loadAgenda failed", e);
        }
    }

    loadAgenda();

    return () => {
        mounted = false;
    };
    }, [id, tabs, activeTab]);

  useEffect(() => {
    if (!id) return;

    let mounted = true;

    async function ensureAgendaBook() {
      try {
        const resp = await apiFetch(
          `/api/agendas/${id}?populate[0]=agenda_book&populate[1]=area&populate[2]=agenda_pre_reg_form`
        );

        const agendaData = resp?.data || resp;
        const existingBook = agendaData?.agenda_book;
        
        if (existingBook) {
          const attrs = existingBook?.agendaAttributes || {};

          const systemFields = [
            "areaNameZh",
            "areaNameId",
            "agendaDate",
            "agendaDateText",
            "lunarDate",
            "agendaSubjectZh",
            "agendaSubjectId",
            "rundownSectionTitleZh",
            "rundownSectionTitleId",
            "personnelSectionTitleZh",
            "personnelSectionTitleId",
            "financeSectionTitleZh",
            "financeSectionTitleId"
          ];

          // cek apakah ada field penting yang kosong
          const missingSystemField = systemFields.some(k => !attrs[k]);

          if (missingSystemField) {
            // rebuild + update
            const newAttrs = await buildInitialAgendaAttributes(
              agendaData
            );

            const merged = {
              ...(existingBook?.agendaAttributes || {}),
              ...newAttrs
            };

            await apiFetch(`/api/agenda-books/${existingBook.documentId}`, {
              method: "PUT",
              body: {
                data: {
                  agendaAttributes: merged
                }
              }
            });

            // reload fresh data
            const fresh = await apiFetch(
              `/api/agenda-books/${existingBook.documentId}?populate=*`
            );

            if (mounted) {
              setAgendaBook(fresh?.data || fresh);
              setAgendaBookReady(true);
            }
          } else {
            if (mounted) {
              setAgendaBook(existingBook);
              setAgendaBookReady(true);
            }
          }
        } else {
          // create agenda-book baru
          const attrs = await buildInitialAgendaAttributes(
            agendaData,
            lunarInfo
          );

          const createResp = await apiFetch(`/api/agenda-books`, {
            method: "POST",
            body: {
              data: {
                agenda: id,
                agendaAttributes: attrs,
                agendaRundowns: [],
                agendaPersonnels: [],
                agendaFinances: []
              }
            }
          });

          if (mounted) {
            setAgendaBook(createResp?.data);
            setAgendaBookReady(true);

            // reload agar relation stabil
            history.replace(`/agendabook/${id}`);
          }
        }
      } catch (e) {
        console.warn("ensureAgendaBook failed", e);
        if (mounted) setAgendaBookReady(true);
      }
    }

    ensureAgendaBook();

    return () => {
      mounted = false;
    };
  }, [id]);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [templateZh, setTemplateZh] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState([]);

  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [saveMode, setSaveMode] = useState("new"); 
  // "new" | "replace"

  return (
    <>
    <TwoColumnLayout
      leftNode={null}
      variant="oneColumn"
      title="Agenda Book"
      showBack
      onBack={() => history.goBack()}
      rightNode={
        <AgendaBookMainPanel
          selectedAgenda={selectedAgenda}
          lunarInfo={lunarInfo}
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          permsReady={permsReady}
          agendaBook={agendaBook}
          onRundownUpdated={handleRundownUpdated}
          onPersonnelUpdated={handlePersonnelUpdated}
          onFinanceUpdated={handleFinanceUpdated}
          agendaBookReady={agendaBookReady}
          id={id}
          history={history}
        />
      }
      headerActions={
        agendaBookReady && (
          <AgendaBookHeaderActions
            agendaBookReady={agendaBookReady}
            hasPreRegForm={!!selectedAgenda?.agenda_pre_reg_form}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            canSaveTemplate={canSaveTemplate}

            setTemplates={setTemplates}
            setSelectedTemplateId={setSelectedTemplateId}
            setTemplateZh={setTemplateZh}
            setTemplateId={setTemplateId}
            setSaveMode={setSaveMode}

            setShowSaveModal={setShowSaveModal}
            setShowLoadModal={setShowLoadModal}
            setShowAttrModal={setShowAttrModal}

            id={id}
            history={history}
          />
        )
      }
    />

    <AgendaBookModals
      showSaveModal={showSaveModal}
      showLoadModal={showLoadModal}
      showAttrModal={showAttrModal}

      setShowSaveModal={setShowSaveModal}
      setShowLoadModal={setShowLoadModal}
      setShowAttrModal={setShowAttrModal}

      templates={templates}
      selectedTemplateId={selectedTemplateId}
      setSelectedTemplateId={setSelectedTemplateId}

      templateZh={templateZh}
      setTemplateZh={setTemplateZh}
      templateId={templateId}
      setTemplateId={setTemplateId}

      saveMode={saveMode}
      setSaveMode={setSaveMode}

      handleSaveTemplate={handleSaveTemplate}
      handleLoadTemplate={handleLoadTemplate}

      agendaBook={agendaBook}
      selectedAgenda={selectedAgenda}
      id={id}
      reloadAgendaBook={reloadAgendaBook}
    />
    </>
  );
}

